import { useRef, useEffect, useState } from "react"
import { StudentNavigationProvider, useStudentNavigation, type StudentPage } from "@/contexts/StudentNavigationContext"
import { StudentHome } from "./StudentHome"
import { StudentClassroom } from "./StudentClassroom"
import { StudentGameboard } from "./StudentGameboard"
import { StudentSupport } from "./StudentSupport"
import { StudentProfile } from "./StudentProfile"
import NotificationPage from "./Notification"
import FooterBar from "./NavBar"
import Documentation from "./Documentation"
import { SelfCorrectionBanner } from "./SelfCorrectionBanner"
import { Bell } from "lucide-react"
import { useApp } from "@/App"
import { useAuth } from "@/contexts/AuthContext"
import { firebaseMessagingService } from "@/config/firebase"
import { studentService } from "@/services/studentService"
import { safeLog } from '@/utils/safeLog';
import { showNativeNotification } from '@/utils/nativeNotification';

// ── First-login permission modal ─────────────────────────────────────────────

function PermissionModal({ authUid, onDone }: { authUid: string | null; onDone: () => void }) {
  const [requesting, setRequesting] = useState(false)

  const allow = async () => {
    setRequesting(true)
    try {
      let granted = false
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        const result = await Notification.requestPermission()
        granted = result === 'granted'
      } else if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        granted = true
      }

      // Register FCM token immediately so the student receives push notifications
      if (granted && authUid) {
        await firebaseMessagingService.getTokenAndRegister(authUid)
      }
    } catch (err) {
      safeLog.error('FCM init error:', err)
    } finally {
      localStorage.setItem('permsAsked', 'true')
      setRequesting(false)
      onDone()
    }
  }

  const skip = () => {
    localStorage.setItem('permsAsked', 'true')
    onDone()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)' }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Hero */}
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 px-6 pt-8 pb-10 text-white text-center relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full" />
          <div className="w-20 h-20 rounded-2xl bg-white/15 border-2 border-white/25 mx-auto mb-4 flex items-center justify-center overflow-hidden">
            <img src="/pwa-maskable-512x512.png" alt="ShieldED Haven" className="w-14 h-14 object-contain" />
          </div>
          <h2 className="text-xl font-bold">Stay safe &amp; informed</h2>
          <p className="text-indigo-200 text-sm mt-1">Allow these so ShieldED Haven can protect and notify you.</p>
        </div>

        {/* Permissions list */}
        <div className="px-6 py-5 space-y-4 -mt-4 relative z-10">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
            <div className="flex items-start gap-3 p-4">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bell className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Push Notifications</p>
                <p className="text-xs text-gray-500 mt-0.5">Alerts from your teacher, admin, and our AI safety system — delivered instantly.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <img src="/pwa-maskable-512x512.png" alt="" className="w-5 h-5 object-contain" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Safety Monitoring</p>
                <p className="text-xs text-gray-500 mt-0.5">Our system watches for harmful content to keep you safe — always on by default.</p>
              </div>
            </div>
          </div>

          <button
            onClick={allow}
            disabled={requesting}
            className="w-full py-3 rounded-2xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
            style={{ border: 'none', cursor: requesting ? 'not-allowed' : 'pointer' }}
          >
            {requesting ? 'Requesting…' : 'Allow & Continue'}
          </button>
          <button
            onClick={skip}
            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main content ─────────────────────────────────────────────────────────────

function StudentContent() {
  const { currentPage, setCurrentPage } = useStudentNavigation()
  const { user }           = useApp()
  const { currentUser }    = useAuth()
  const scrollRef          = useRef<HTMLDivElement>(null)
  const [showPermModal, setShowPermModal] = useState(() => localStorage.getItem('permsAsked') !== 'true')
  const [unreadCount, setUnreadCount]    = useState(0)
  const [dm, setDm]                      = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  // ── React to dark mode toggle from StudentProfile ──────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    }
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  // Tracks notification IDs already surfaced as OS popups (persisted across page refreshes)
  const shownIdsRef = useRef<Set<string>>(
    new Set(JSON.parse(localStorage.getItem('sh_shown_notif_ids') || '[]'))
  )
  // Tracks previous page so we can re-poll after leaving the notifications tab
  const prevPageRef = useRef(currentPage)

  const authUid = sessionStorage.getItem('authUid') ?? currentUser?.id ?? user?.id ?? null

  // ── Reset scroll to top on every page change ──────────────────────────────
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' })
  }, [currentPage])

  // ── Poll notification inbox: update badge + show OS popups ────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const data = await studentService.getNotifications()
        const count = data.unreadCount ?? 0
        setUnreadCount(count)
        // Sync badge with Electron dock / taskbar
        ;window.electronAPI?.updateBadge?.(count)

        // Show OS notification popup for each new unread item
        const canShow =
          !!window.electronAPI?.showNotification ||
          (typeof Notification !== 'undefined' && Notification.permission === 'granted')
        if (canShow) {
          const newIds: string[] = []
          for (const n of data.notifications) {
            if (n.status !== 'unread' || shownIdsRef.current.has(n.id)) continue
            showNativeNotification(n.title || 'ShieldED Haven', {
              body: n.message,
              icon: '/pwa-maskable-512x512.png',
              badge: '/pwa-maskable-512x512.png',
              tag: `sh-notif-${n.id}`,
              requireInteraction: true,
              onClick: () => { window.focus(); setCurrentPage('notifications') },
            })
            shownIdsRef.current.add(n.id)
            newIds.push(n.id)
          }
          if (newIds.length > 0) {
            localStorage.setItem(
              'sh_shown_notif_ids',
              JSON.stringify(Array.from(shownIdsRef.current).slice(-200))
            )
          }
        }
      } catch { /* non-fatal */ }
    }

    poll()
    const id = setInterval(poll, 60_000)
    return () => clearInterval(id)
  }, [setCurrentPage])

  // ── Re-poll after leaving the notifications page (badge may have dropped) ─
  useEffect(() => {
    if (prevPageRef.current === 'notifications' && currentPage !== 'notifications') {
      studentService.getNotifications().then(data => {
        const count = data.unreadCount ?? 0
        setUnreadCount(count)
        ;window.electronAPI?.updateBadge?.(count)
      }).catch(() => { /* non-fatal */ })
    }
    prevPageRef.current = currentPage
  }, [currentPage])

  // ── Listen for navigation events dispatched by the FCM foreground handler ──
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const page = (e as CustomEvent<{ page: string }>).detail?.page
      if (page) setCurrentPage(page as StudentPage)
    }
    window.addEventListener('student-navigate', onNavigate)
    return () => window.removeEventListener('student-navigate', onNavigate)
  }, [setCurrentPage])

  // ── Re-poll badge when a new push notification arrives in the foreground ──
  useEffect(() => {
    const onNewNotif = async () => {
      try {
        const data = await studentService.getNotifications()
        const count = data.unreadCount ?? 0
        setUnreadCount(count)
        ;window.electronAPI?.updateBadge?.(count)
      } catch { /* non-fatal */ }
    }
    window.addEventListener('new-student-notification', onNewNotif)
    return () => window.removeEventListener('new-student-notification', onNewNotif)
  }, [])

  // ── Ensure FCM token is registered on every load when permission is granted ─
  // Handles returning students who already granted permission but have no token
  // saved yet (e.g. logged in before this feature, or token was cleared).
  useEffect(() => {
    if (!authUid) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return
    firebaseMessagingService.getTokenAndRegister(authUid).catch(() => {/* non-fatal */})
  }, [authUid])

  const initials = user?.name
    ? user.name.split(' ').map((p: string) => p[0]).slice(0, 2).join('').toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? 'S'

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home':          return <StudentHome />
      case 'classroom':     return <StudentClassroom />
      case 'games':         return <StudentGameboard />
      case 'safety':        return <StudentSupport />
      case 'profile':       return <StudentProfile />
      case 'notifications': return <NotificationPage />
      case 'documentation': return <Documentation darkMode={dm} />
      default:              return <StudentHome />
    }
  }

  return (
    /*
     * Outer shell uses h-screen + overflow-hidden so the header and footer
     * are always visible and only the middle pane scrolls — works identically
     * in the Electron window and in the browser.
     */
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: dm ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' : 'linear-gradient(135deg, #dde1ff 0%, #c7d2fe 30%, #bfdbfe 70%, #e0f2fe 100%)' }}>

      {/* ── Header — full-width bar, content centred ── */}
      <header className={`flex-shrink-0 backdrop-blur-xl border-b shadow-sm z-40 ${dm ? 'bg-slate-900/90 border-slate-700/60' : 'bg-white/85 border-white/50'}`}>
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">

          {/* Logo / home link */}
          <button
            onClick={() => setCurrentPage('home')}
            style={{ background: 'none', border: 'none', outline: 'none', boxShadow: 'none' }}
            className="hover:opacity-75 transition-opacity ml-[-1rem]"
          >
            <img
              src={dm ? '/shlogo3.png' : '/shlogo.png'}
              alt="ShieldEd Haven"
              className="h-32 w-auto object-contain transition-all duration-300"
            />
          </button>

          {/* Right actions — no gap between the two icon buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

            {/* Bell */}
            <button
              onClick={() => setCurrentPage('notifications')}
              style={{ background: 'none', border: 'none', outline: 'none', boxShadow: 'none', padding: '6px', position: 'relative', cursor: 'pointer' }}
              className="rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Bell
                className={`w-6 h-6 transition-colors ${
                  currentPage === 'notifications'
                    ? 'text-indigo-400'
                    : dm ? 'text-slate-400' : 'text-gray-500'
                }`}
              />
              {/* Dynamic badge — hidden when no unread notifications */}
              {unreadCount > 0 && (
                <span
                  style={{ position: 'absolute', top: 2, right: 2 }}
                  className="bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center ring-2 ring-white px-0.5"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* Avatar */}
            <button
              onClick={() => setCurrentPage('profile')}
              style={{ background: 'none', border: 'none', outline: 'none', boxShadow: 'none', padding: 4, cursor: 'pointer' }}
              className="transition-transform hover:scale-105"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-100 border-2 border-indigo-200 flex items-center justify-center">
                <span className="text-indigo-700 text-xs font-bold leading-none">{initials}</span>
              </div>
            </button>

          </div>
        </div>
      </header>

      {/*
       * ── Scrollable content pane ──────────────────────────────────────────
       * flex-1 + overflow-y-auto: only this div scrolls.
       * The header and footer nav are always visible above and below.
       * max-w-2xl centres the content on wide screens (browser / Electron).
       */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-2xl mx-auto w-full">

          {/* Self-correction alerts (shown above page content) */}
          <div className="px-4 pt-3">
            <SelfCorrectionBanner />
          </div>

          {/* Page content — bottom-pad clears the fixed nav bar */}
          <main className="pb-24">
            {renderCurrentPage()}
          </main>

        </div>
      </div>

      {/* ── Bottom nav ── */}
      <FooterBar />

      {/* ── First-login permission modal ── */}
      {showPermModal && <PermissionModal authUid={authUid} onDone={() => setShowPermModal(false)} />}
    </div>
  )
}

export function StudentDashboard() {
  return (
    <StudentNavigationProvider>
      <StudentContent />
    </StudentNavigationProvider>
  )
}
